import { useTranslation } from 'react-i18next';
import { Sparkles, LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { ProjectType } from '@/lib/projectType';

export type { ProjectType };

interface ProjectTypeSelectorProps {
  value: ProjectType;
  onChange: (value: ProjectType) => void;
  className?: string;
}

export function ProjectTypeSelector({ value, onChange, className }: ProjectTypeSelectorProps) {
  const { t } = useTranslation();

  return (
    <div className={cn('flex flex-col md:flex-row gap-2', className)}>
      {/* Just UI */}
      <button
        type="button"
        onClick={() => onChange('vanilla')}
        className={cn(
          'relative flex items-center gap-4 px-4 py-3 rounded-xl border-2 transition-all duration-200 text-left flex-1',
          value === 'vanilla'
            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
            : 'border-border hover:border-primary/50 hover:bg-accent/50'
        )}
      >
        <LayoutGrid className={cn(
          'w-5 h-5 shrink-0',
          value === 'vanilla' ? 'text-primary' : 'text-muted-foreground'
        )} />
        <div className="flex flex-col min-w-0">
          <span className={cn(
            'text-sm font-medium',
            value === 'vanilla' ? 'text-primary' : 'text-foreground'
          )}>
            {t('projectTypeJustUI')}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('projectTypeJustUIDescription')}
          </span>
        </div>
      </button>

      {/* UI + Superpowers (Nostr) */}
      <button
        type="button"
        onClick={() => onChange('nostr')}
        className={cn(
          'relative flex items-center gap-4 px-4 py-3 rounded-xl border-2 transition-all duration-200 text-left flex-1',
          value === 'nostr'
            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
            : 'border-border hover:border-primary/50 hover:bg-accent/50'
        )}
      >
        <Sparkles className={cn(
          'w-5 h-5 shrink-0',
          value === 'nostr' ? 'text-primary' : 'text-muted-foreground'
        )} />
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              'text-sm font-medium',
              value === 'nostr' ? 'text-primary' : 'text-foreground'
            )}>
              {t('projectTypeSuperpowers')}
            </span>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
              {t('projectTypePoweredBy')}
            </Badge>
          </div>
          <span className="text-xs text-muted-foreground">
            {t('projectTypeSuperpowersDescription')}
          </span>
        </div>
      </button>
    </div>
  );
}
